import React from 'react'
import PropTypes from 'prop-types'
import { Row, Col } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faThumbtack } from '@fortawesome/free-solid-svg-icons'
import { twoDigitMoney, noDigitMoney } from 'helpers/utils'
import { formatDateSlash } from 'helpers/datetime'
import { relabelAdditionalFields } from 'transactions/drawer/workflow-additional-fields'
import { formatSecondsAsDays } from 'shared/column-filters/timeInQueueUnit'
import MutedText from 'shared/forms/muted-text'

const TransactionDetailGroup = ({ groupName, fields, onPinField, pinnedFieldIds, vendor }) => {
  const displayedFields = relabelAdditionalFields(fields)

  const formatValue = (field) => {
    const { value, display_as, database_column_name } = field

    // Handle null or undefined values
    if (value === null || value === undefined) {
      return '-'
    }

    // For basic vendor_id fields, display vendor external_id instead of UUID
    if (database_column_name?.endsWith('_vendor_id') && vendor?.external_id) {
      return vendor.external_id
    }

    // Format based on display_as type
    switch (display_as) {
      case 'money':
        return noDigitMoney(value)
      case 'two_digit_money':
        return twoDigitMoney(value)
      case 'date':
        return formatDateSlash(value)
      case 'percent':
        return `${value}%`
      case 'boolean':
        return value ? 'Yes' : 'No'
      case 'time_in_queue':
        return formatSecondsAsDays(value) || value
      default:
        return value
    }
  }

  const isPinned = (field) => {
    return pinnedFieldIds && pinnedFieldIds.has(field.database_column_name)
  }

  return (
    <div className="w-100">
      <Row>
        {displayedFields.map((field, index) => {
          const pinned = isPinned(field)

          return (
            <Col key={`${field.database_column_name}-${index}`} xs={12} sm={6} md={6} lg={3} className="mb-3">
              <div className="transaction-field-item">
                <div className="d-flex align-items-center">
                  <MutedText as="div" className="small">{field.display_name}</MutedText>
                  {onPinField && (
                    <span
                      className={`pin-icon ms-1 ${pinned ? 'text-primary pinned' : 'text-muted'}`}
                      title={pinned ? 'Unpin field' : 'Pin field'}
                      style={{ fontSize: '0.75rem', cursor: 'pointer' }}
                      onClick={() => onPinField(field, pinned)}
                    >
                      <FontAwesomeIcon icon={faThumbtack} />
                    </span>
                  )}
                </div>
                <div>{formatValue(field)}</div>
              </div>
            </Col>
          )
        })}
      </Row>
      <style jsx>{`
        .transaction-field-item {
          padding: 0.5rem;
          border-radius: 4px;
        }

        .transaction-field-item .pin-icon {
          opacity: 0;
          transition: opacity 0.2s ease;
        }

        .transaction-field-item .pin-icon.pinned {
          opacity: 1;
        }

        .transaction-field-item:hover .pin-icon {
          opacity: 1;
        }
      `}</style>
    </div>
  )
}

TransactionDetailGroup.propTypes = {
  groupName: PropTypes.string.isRequired,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      display_name: PropTypes.string.isRequired,
      value: PropTypes.any,
      display_as: PropTypes.string.isRequired,
      database_column_name: PropTypes.string.isRequired
    })
  ).isRequired,
  onPinField: PropTypes.func,
  pinnedFieldIds: PropTypes.instanceOf(Set),
  vendor: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    external_id: PropTypes.string
  })
}

export default TransactionDetailGroup
